/* ============================================================
   MARAIS — marais-search.js
   ────────────────────────────────────────────────────────────
   Powers four discrete search surfaces:

     1. Header search panel (slide-down overlay from /, any page)
        ↳ Trending products + recent searches by default
        ↳ Live results as the user types (debounced 220ms)
        ↳ Gender tabs flip between Womenswear / Menswear trending

     2. Mobile search trigger (bottom nav button)
        ↳ Shares state with header panel via window.openSearch

     3. Search results page (templates/search.liquid) autocomplete
        ↳ Inline dropdown beneath the hero input

     4. Search results page tab filtering
        ↳ All / Products / Articles / Pages tabs switch sections

   Loaded globally via theme.liquid:720 (audit flagged this as a
   perf opportunity — gate to search-needing pages only).

   Predictive endpoint:
     /search/suggest?q=…&resources[type]=product&resources[limit]=5
       &section_id=predictive-search
     Returns the predictive-search.liquid section HTML which we
     parse for [data-ps="products"] and [data-ps="queries"] regions.
   ============================================================ */

(function () {
  'use strict';

  /* Section ID matches sections/predictive-search.liquid */
  var SECTION_ID = 'predictive-search';
  /* localStorage key — recent searches persist across visits */
  var RECENT_KEY = 'mh-recent-searches';
  var MAX_RECENT = 5;
  /* localStorage key — recently viewed products */
  var VIEWED_KEY = 'mh-recent-products';
  var MAX_VIEWED = 12;
  /* localStorage key — current gender tab (women/men). Shared with the
     trending swap below and now also drives query scoping. */
  var GENDER_KEY = 'mh-gender';

  /* ── Gender scoping ──
     Mirrors the logic in marais-gendered-search.liquid: the active
     gender tab prepends a Shopify-search qualifier to predictive
     queries and adds a filter param (or swaps the collection URL)
     to results-page submits. Config comes from theme.liquid as
     window.MARAIS_GENDER_SCOPE. */
  function getGenderConfig() {
    var c = window.MARAIS_GENDER_SCOPE || {};
    return {
      signal:      c.signal      || 'tag',
      men_value:   c.men_value   || 'Mens',
      women_value: c.women_value || 'Womens',
    };
  }
  function getActiveGender() {
    try {
      var g = localStorage.getItem(GENDER_KEY);
      return (g === 'men' || g === 'women') ? g : 'women';
    } catch (e) { return 'women'; }
  }
  function genderValue() {
    var c = getGenderConfig();
    return getActiveGender() === 'men' ? c.men_value : c.women_value;
  }
  /* Prepend a `<signal>:"<value>" AND ` qualifier so /search/suggest
     scopes to the active gender. Collection mode normally relies on
     the URL path for scoping (not a query qualifier), but on a store
     where /search/suggest can be tag-scoped while /search can't, the
     config can also set `predictive_tag.{men,women}` to drive ONLY the
     predictive dropdown's qualifier — submit still uses the collection
     URL via applyGenderToForm. */
  function scopedQuery(q) {
    var c = getGenderConfig();
    if (c.signal === 'tag')          return 'tag:"' + genderValue() + '" AND ' + q;
    if (c.signal === 'product_type') return 'product_type:"' + genderValue() + '" AND ' + q;
    if (c.signal === 'vendor')       return 'vendor:"' + genderValue() + '" AND ' + q;
    // 'collection' — fall through to optional predictive_tag override.
    var pt = c.predictive_tag;
    if (pt && pt[getActiveGender()]) {
      return 'tag:"' + pt[getActiveGender()] + '" AND ' + q;
    }
    return q;
  }
  /* Update every wishlist-style search form (header + mobile drawer)
     so its submit lands on the right URL for the active gender. */
  function applyGenderToForm(form) {
    if (!form) return;
    var c = getGenderConfig();
    var v = genderValue();
    var hidden = form.querySelector('[data-mh-search-filter]');
    var _lp = window._mhLocalePrefix || '';
    if (c.signal === 'collection') {
      form.action = _lp + '/collections/' + encodeURIComponent(v) + '/search';
      if (hidden) { hidden.name = ''; hidden.value = ''; } // unused
      return;
    }
    form.action = _lp + '/search';
    var paramName = 'filter.p.tag';
    if (c.signal === 'product_type') paramName = 'filter.p.product_type';
    else if (c.signal === 'vendor')  paramName = 'filter.p.vendor';
    if (hidden) {
      hidden.name = paramName;
      hidden.value = v;
    }
  }
  function applyGenderToAllForms() {
    document.querySelectorAll('[data-mh-search-form]').forEach(applyGenderToForm);
  }

  /* ── Predictive fetch ──
     Uses Shopify's Section Rendering API: pass section_id to get
     just that section's rendered HTML rather than a full page.
     Then we DOMParser the response and extract the inner section.
     Query is gender-scoped via the active tab when applicable. */
  function fetchPredictive(q, callback) {
    var url = (window._mhLocalePrefix || '') + '/search/suggest?q=' + encodeURIComponent(scopedQuery(q))
      + '&resources[type]=product'
      + '&resources[limit]=5'
      + '&section_id=' + SECTION_ID;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('#shopify-section-' + SECTION_ID);
        callback(inner ? inner.innerHTML.trim() : '');
      })
      .catch(function () { callback(''); });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Recent searches (localStorage) ──
     Stored as JSON array, newest first. saveSearch dedupes by
     filtering then unshifting; capped at MAX_RECENT (5).
     try/catch guards against localStorage being unavailable
     (private browsing, quota exceeded, disabled). */
  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  }
  function saveSearch(q) {
    var arr = getRecent().filter(function (t) { return t !== q; });
    arr.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, MAX_RECENT)));
  }
  function removeRecent(q, renderFn) {
    localStorage.setItem(RECENT_KEY, JSON.stringify(getRecent().filter(function (t) { return t !== q; })));
    renderFn();
  }

  /* ── Recently viewed products (localStorage) ──
     Populated by the PDP snapshot blob `#mhRecentlyViewedSnapshot`.
     Dedupes by handle; newest first; capped at MAX_VIEWED. */
  function getViewed() {
    try { return JSON.parse(localStorage.getItem(VIEWED_KEY)) || []; } catch (e) { return []; }
  }
  function saveViewedFromSnapshot() {
    var el = document.getElementById('mhRecentlyViewedSnapshot');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    if (!data || !data.handle) return;
    var arr = getViewed().filter(function (p) { return p && p.handle !== data.handle; });
    arr.unshift(data);
    try { localStorage.setItem(VIEWED_KEY, JSON.stringify(arr.slice(0, MAX_VIEWED))); } catch (e) {}
  }
  function renderViewedInto(container, label) {
    if (!container) return;
    var items = getViewed();
    if (!items.length) {
      container.hidden = true;
      container.innerHTML = '';
      if (label) label.hidden = true;
      return;
    }
    container.hidden = false;
    if (label) label.hidden = false;
    container.innerHTML = items.map(function (p) {
      var img = p.image
        ? '<img src="' + escHtml(p.image) + '" alt="' + escHtml(p.title || '') + '" loading="lazy">'
        : '<div class="mh-viewed-card__ph"></div>';
      var compare = p.comparePrice ? '<s class="mh-viewed-card__was">' + escHtml(p.comparePrice) + '</s>' : '';
      var price   = p.price ? '<span class="mh-viewed-card__price">' + escHtml(p.price) + '</span>' : '';
      var vendor  = p.vendor ? '<p class="mh-viewed-card__vendor">' + escHtml(p.vendor) + '</p>' : '';
      return '<a class="mh-viewed-card" href="' + escHtml(p.url || '#') + '">'
        + '<div class="mh-viewed-card__img">' + img + '</div>'
        + vendor
        + '<p class="mh-viewed-card__title">' + escHtml(p.title || '') + '</p>'
        + (compare || price ? '<p class="mh-viewed-card__pr">' + compare + price + '</p>' : '')
        + '</a>';
    }).join('');
  }

  /* ── Header search panel ── */
  function initHeaderSearch() {
    var trigger  = document.getElementById('mh-search-trigger');
    var panel    = document.getElementById('mh-search-panel');
    var overlay  = document.getElementById('mh-search-overlay');
    var input    = document.getElementById('mh-search-input');
    var closeBtn = document.getElementById('mh-search-close');
    if (!panel || !input) return;

    /* timer    : debounce handle for input events
       activeQuery : last query the user typed; used to discard
                     stale fetch responses (race condition guard).
       trendingDone: ensure trending section is populated only once
                     per page load even if user opens panel repeatedly. */
    var timer        = null;
    var activeQuery  = '';
    var trendingDone = false;

    /* ── State helpers ── */
    function showDefault() {
      document.getElementById('mh-sp-default-left').hidden  = false;
      document.getElementById('mh-sp-query-left').hidden    = true;
      document.getElementById('mh-sp-default-right').hidden = false;
      document.getElementById('mh-sp-query-right').hidden   = true;
    }

    function showQuery() {
      document.getElementById('mh-sp-default-left').hidden  = true;
      document.getElementById('mh-sp-query-left').hidden    = false;
      document.getElementById('mh-sp-default-right').hidden = true;
      document.getElementById('mh-sp-query-right').hidden   = false;
    }

    /* ── Gender tabs ──
       Drives both the trending swap below AND the gender qualifier
       prepended to predictive queries / appended to form submits. */
    function setActiveTab(g) {
      document.querySelectorAll('.mh-sp-tab').forEach(function (t) {
        t.classList.toggle('is-active', t.dataset.spG === g);
      });
      var elW = document.getElementById('mh-sp-trending-women');
      var elM = document.getElementById('mh-sp-trending-men');
      if (elW) elW.hidden = (g !== 'women');
      if (elM) elM.hidden = (g !== 'men');
      applyGenderToAllForms();
    }

    document.querySelectorAll('.mh-sp-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var g = this.dataset.spG;
        localStorage.setItem('mh-gender', g);
        setActiveTab(g);
        // If a query is already being shown, re-fetch under the new
        // gender so the live results update without the user having
        // to retype.
        if (activeQuery && activeQuery.length >= 2) runSearch(activeQuery);
      });
    });

    /* ── Trending population (once per page load) ──
       Trending content is server-rendered by
       sections/marais-mega-trending{,-men}.liquid into hidden
       <div>s with ids #mh-trending-src-women / -men. We clone
       their innerHTML into the visible search-panel slots.
       Same source feeds the mega-menu trending panels. */
    function populateTrending() {
      var srcW = document.getElementById('mh-trending-src-women');
      var srcM = document.getElementById('mh-trending-src-men');
      var tW   = document.getElementById('mh-sp-trending-women');
      var tM   = document.getElementById('mh-sp-trending-men');
      if (srcW && tW) tW.innerHTML = srcW.innerHTML;
      if (srcM && tM) tM.innerHTML = srcM.innerHTML;
      trendingDone = true;
    }

    /* ── Recent searches ── */
    function renderRecent() {
      var list   = document.getElementById('mh-sp-recent-list');
      var recent = getRecent();
      if (!list) return;
      var label = document.querySelector('#mh-sp-default-right .mh-sp-label');
      if (!recent.length) {
        list.innerHTML = '';
        if (label) label.style.display = 'none';
        return;
      }
      if (label) label.style.display = '';
      list.innerHTML = recent.map(function (t) {
        return '<li>'
          + '<a href="' + (window._mhLocalePrefix || '') + '/search?q=' + encodeURIComponent(t) + '&type=product" class="mh-sp-recent-term">' + escHtml(t) + '</a>'
          + '<button class="mh-sp-recent-remove" data-term="' + escHtml(t) + '" aria-label="Remove ' + escHtml(t) + '">×</button>'
          + '</li>';
      }).join('');
      list.querySelectorAll('.mh-sp-recent-remove').forEach(function (btn) {
        btn.addEventListener('click', function () { removeRecent(this.dataset.term, renderRecent); });
      });
    }

    /* ── Open / close ── */
    function openPanel() {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (!trendingDone) populateTrending();
      setActiveTab(localStorage.getItem('mh-gender') || 'women');
      renderViewedInto(document.getElementById('mh-sp-viewed'), document.getElementById('mh-sp-viewed-label'));
      renderRecent();
      showDefault();
      input.focus();
    }

    function closePanel() {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      input.value = '';
      activeQuery = '';
      clearTimeout(timer);
    }

    window.openSearch = openPanel;
    if (trigger) trigger.addEventListener('click', openPanel);
    if (closeBtn) closeBtn.addEventListener('click', closePanel);
    if (overlay)  overlay.addEventListener('click', closePanel);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel();
    });

    /* ── Search execution ──
       Race guard: by the time the fetch resolves, the user may
       have typed more. We compare q vs activeQuery and bail if
       the response is stale. Without this, slow responses for
       earlier queries can overwrite newer results. */
    function runSearch(q) {
      activeQuery = q;
      showQuery();
      var labelEl = document.getElementById('mh-sp-query-label');
      if (labelEl) labelEl.textContent = q;
      fetchPredictive(q, function (html) {
        if (q !== activeQuery) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var productsEl = tmp.querySelector('[data-ps="products"]');
        var queriesEl  = tmp.querySelector('[data-ps="queries"]');
        var grid = document.getElementById('mh-sp-product-grid');
        var sugg = document.getElementById('mh-sp-suggestions');
        if (grid) grid.innerHTML = productsEl ? productsEl.innerHTML : '';
        if (sugg) sugg.innerHTML = queriesEl  ? queriesEl.innerHTML  : '';
      });
    }

    /* ── Input handler (debounced 220ms) ──
       <2 chars reverts to trending/recent default view to avoid
       firing requests for "a" or "an" that return noise. */
    input.addEventListener('input', function () {
      var q = this.value.trim();
      clearTimeout(timer);
      activeQuery = q;
      if (q.length < 2) { showDefault(); return; }
      timer = setTimeout(function () { runSearch(q); }, 220);
    });

    /* ── Form submit — save term and navigate to search page ── */
    var form = panel.querySelector('form');
    if (form) {
      form.addEventListener('submit', function () {
        var q = input.value.trim();
        if (q) saveSearch(q);
      });
    }
  }

  /* ── Search page autocomplete ── */
  function initAutocomplete() {
    var input    = document.querySelector('.search-hero__input');
    var dropdown = document.querySelector('.search-autocomplete');
    if (!input || !dropdown) return;

    var timer = null;
    var activeQuery = '';

    input.addEventListener('input', function () {
      var q = this.value.trim();
      clearTimeout(timer);
      activeQuery = q;
      if (q.length < 2) { closeDropdown(); return; }
      timer = setTimeout(function () {
        fetchPredictive(q, function (html) {
          if (q !== activeQuery) return;
          if (html) {
            var tmp = document.createElement('div');
            tmp.innerHTML = html;
            var productsEl = tmp.querySelector('[data-ps="products"]');
            if (productsEl && productsEl.innerHTML.trim()) {
              dropdown.innerHTML = productsEl.innerHTML;
              dropdown.classList.add('is-open');
            } else {
              closeDropdown();
            }
          } else {
            closeDropdown();
          }
        });
      }, 220);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDropdown();
    });

    document.addEventListener('click', function (e) {
      if (!input.contains(e.target) && !dropdown.contains(e.target)) closeDropdown();
    });

    function closeDropdown() {
      dropdown.classList.remove('is-open');
      dropdown.innerHTML = '';
    }
  }

  /* ── Search page tab filtering ── */
  function initTabs() {
    var tabs     = document.querySelectorAll('.search-tab');
    var sections = document.querySelectorAll('.search-results__section');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = this.dataset.tab;
        tabs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
        sections.forEach(function (sec) {
          sec.style.display = (target === 'all' || sec.dataset.type === target) ? '' : 'none';
        });
      });
    });
  }

  /* ── Mobile search trigger ── */
  function initMobileSearch() {
    var mobTrigger = document.getElementById('mh-mob-search-trigger');
    if (!mobTrigger) return;
    mobTrigger.addEventListener('click', function () {
      var mobNav     = document.getElementById('mh-mob-nav');
      var mobOverlay = document.getElementById('mh-mob-overlay');
      if (mobNav)     { mobNav.classList.remove('is-open'); mobNav.setAttribute('aria-hidden', 'true'); }
      if (mobOverlay) { mobOverlay.classList.remove('is-visible'); }
      document.body.style.overflow = '';
      if (window.openSearch) window.openSearch();
    });
  }

  /* Mobile hub "Recent Searches" renderer (analogous to renderRecent
     but writing into the mobile DOM). */
  function renderRecentMobile() {
    var list  = document.getElementById('mh-search-mob-recent');
    var label = document.getElementById('mh-search-mob-recent-label');
    if (!list) return;
    var recent = getRecent();
    if (!recent.length) {
      list.hidden = true; list.innerHTML = '';
      if (label) label.hidden = true;
      return;
    }
    list.hidden = false;
    if (label) label.hidden = false;
    list.innerHTML = recent.map(function (t) {
      return '<li>'
        + '<a href="' + (window._mhLocalePrefix || '') + '/search?q=' + encodeURIComponent(t) + '&type=product" class="mh-search-mob__recent-term">' + escHtml(t) + '</a>'
        + '<button class="mh-search-mob__recent-remove" data-term="' + escHtml(t) + '" aria-label="Remove ' + escHtml(t) + '">×</button>'
        + '</li>';
    }).join('');
    list.querySelectorAll('.mh-search-mob__recent-remove').forEach(function (btn) {
      btn.addEventListener('click', function () { removeRecent(this.dataset.term, renderRecentMobile); });
    });
  }

  /* Render the recently viewed + recent searches for whichever surface
     is currently in the DOM. Called by both the desktop panel opener
     and the mobile hub opener (theme.liquid). */
  /* /search page "Recent Searches" renderer. Mirrors the desktop panel
     version but writes into the page-level container. */
  function renderRecentSearchPage() {
    var list  = document.getElementById('mh-search-page-recent');
    var label = document.getElementById('mh-search-page-recent-label');
    if (!list) return;
    var recent = getRecent();
    if (!recent.length) {
      list.hidden = true; list.innerHTML = '';
      if (label) label.hidden = true;
      return;
    }
    list.hidden = false;
    if (label) label.hidden = false;
    list.innerHTML = recent.map(function (t) {
      return '<li>'
        + '<a href="' + (window._mhLocalePrefix || '') + '/search?q=' + encodeURIComponent(t) + '&type=product" class="search-page__recent-term">' + escHtml(t) + '</a>'
        + '<button class="search-page__recent-remove" data-term="' + escHtml(t) + '" aria-label="Remove ' + escHtml(t) + '">×</button>'
        + '</li>';
    }).join('');
    list.querySelectorAll('.search-page__recent-remove').forEach(function (btn) {
      btn.addEventListener('click', function () { removeRecent(this.dataset.term, renderRecentSearchPage); });
    });
  }

  window.mhRenderSearchMeta = function () {
    renderViewedInto(document.getElementById('mh-sp-viewed'),           document.getElementById('mh-sp-viewed-label'));
    renderViewedInto(document.getElementById('mh-search-mob-viewed'),   document.getElementById('mh-search-mob-viewed-label'));
    renderViewedInto(document.getElementById('mh-search-page-viewed'),  document.getElementById('mh-search-page-viewed-label'));
    renderRecentMobile();
    renderRecentSearchPage();
  };

  document.addEventListener('DOMContentLoaded', function () {
    saveViewedFromSnapshot();
    // Set the gender filter on every search form upfront so a submit
    // before the user opens the drawer still scopes to the active
    // tab (localStorage holds the last-picked gender across visits).
    applyGenderToAllForms();
    initHeaderSearch();
    initAutocomplete();
    initTabs();
    initMobileSearch();
    // Populate the /search page rails immediately if those containers exist.
    if (document.getElementById('mh-search-page-viewed') || document.getElementById('mh-search-page-recent')) {
      // Save the current ?q= so that landing on /search via form submit,
      // autocomplete click, or direct URL all contribute to recent searches.
      // The header-panel form has its own submit handler, but submissions
      // from the search-page hero form (and direct navigation) weren't
      // being recorded before this.
      try {
        var qParam = new URLSearchParams(location.search).get('q');
        if (qParam && qParam.trim()) saveSearch(qParam.trim());
      } catch (e) { /* URLSearchParams unavailable */ }
      window.mhRenderSearchMeta();
    }
  });

}());
